import { useState } from "react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { keyFromName } from "@/tenax/tasks/taskData";
import {
  useGoodsOutReasons,
  useLoanPurposes,
  type CatalogRow,
} from "./racketData";

// Reasons of the goods out and purposes of the loans, edited by the team
// (D-031, D-040): renamed, turned off, flag. A row in use cannot be
// deleted (the database refuses), so there is no delete button.
export const RacketCatalogSettings = () => {
  const translate = useTranslate();
  const { data: reasons = [] } = useGoodsOutReasons();
  const { data: purposes = [] } = useLoanPurposes();
  return (
    <section
      aria-label={translate("tenax.rackets.catalog.title")}
      className="flex flex-col gap-4"
    >
      <h2 className="text-lg font-semibold">
        {translate("tenax.rackets.catalog.title")}
      </h2>
      <CatalogList
        resource="loan_purposes"
        title={translate("tenax.rackets.catalog.purposes")}
        flag="tracks_outcome"
        flagLabel={translate("tenax.rackets.catalog.tracks_outcome")}
        rows={purposes}
      />
      <CatalogList
        resource="goods_out_reasons"
        title={translate("tenax.rackets.catalog.reasons")}
        flag="needs_recipient"
        flagLabel={translate("tenax.rackets.catalog.needs_recipient")}
        rows={reasons}
      />
    </section>
  );
};

const CatalogList = <T extends CatalogRow>({
  resource,
  title,
  flag,
  flagLabel,
  rows,
}: {
  resource: string;
  title: string;
  flag: keyof T & string;
  flagLabel: string;
  rows: T[];
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState("");

  const run = async (action: () => Promise<unknown>) => {
    try {
      await action();
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
    await queryClient.invalidateQueries({ queryKey: [resource] });
  };
  const update = (row: T, data: Partial<T>) =>
    run(() =>
      dataProvider.update(resource, { id: row.id, data, previousData: row }),
    );
  const add = () => {
    const name = newName.trim();
    if (!name) return;
    setNewName("");
    return run(() =>
      dataProvider.create(resource, {
        data: { key: keyFromName(name), name, position: rows.length + 1 },
      }),
    );
  };

  return (
    <div aria-label={title} role="group" className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">{title}</h3>
      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <li
            key={row.id}
            aria-label={row.name}
            className="flex flex-wrap items-center gap-3 rounded-md border p-2"
          >
            <Input
              aria-label={translate("tenax.rackets.catalog.name")}
              defaultValue={row.name}
              className="min-w-40 flex-1"
              onBlur={(event) =>
                event.target.value.trim() && event.target.value !== row.name
                  ? update(row, {
                      name: event.target.value.trim(),
                    } as Partial<T>)
                  : undefined
              }
            />
            <label className="flex items-center gap-1.5 text-sm">
              <Checkbox
                checked={Boolean(row[flag])}
                onCheckedChange={(value) =>
                  update(row, { [flag]: value === true } as Partial<T>)
                }
              />
              {flagLabel}
            </label>
            <label className="flex items-center gap-1.5 text-sm">
              <Checkbox
                checked={row.active}
                onCheckedChange={(value) =>
                  update(row, { active: value === true } as Partial<T>)
                }
              />
              {translate("tenax.rackets.catalog.active")}
            </label>
          </li>
        ))}
      </ul>
      <div className="flex items-end gap-2">
        <div className="flex flex-1 flex-col gap-1">
          <Label htmlFor={`${resource}-new`}>
            {translate("tenax.rackets.catalog.new")}
          </Label>
          <Input
            id={`${resource}-new`}
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                add();
              }
            }}
          />
        </div>
        <Button type="button" variant="outline" onClick={add}>
          {translate("ra.action.add")}
        </Button>
      </div>
    </div>
  );
};
